import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ElementType, FC, ReactNode } from "react";
import { ALPHA, FONT_SIZE, LINE_HEIGHT, MOTION, SYSTEM_BANNER } from "../../../theme/tokens";

/**
 * Semantic tone for a `SystemBanner`. Each maps onto the theme status palette — never a hardcoded
 * hex — so banners read as one family with `MetaChip` / `CandidateStatusChip`.
 */
export type SystemBannerTone = "info" | "success" | "warning" | "error" | "primary";

/**
 * `stacked` puts the description under the title. `inline` runs them as one sentence with a bold lead
 * ("**Search expanded: Urgent.** Reaching more people, sooner."), for a one-line state banner over a list.
 */
export type SystemBannerLayout = "stacked" | "inline";

/**
 * `solid` is the tinted banner. `strong` fills a notch deeper, for a state that should read as special (a holiday
 * schedule in effect). `pending` is a plain surface with a dashed edge, for something planned that hasn't taken
 * effect yet (a holiday schedule not yet applied).
 */
export type SystemBannerEmphasis = "solid" | "strong" | "pending";

const FILL_ALPHA: Record<Exclude<SystemBannerEmphasis, "pending">, number> = { solid: SYSTEM_BANNER.FILL_ALPHA, strong: SYSTEM_BANNER.STRONG_FILL_ALPHA };

type Props = {
	/** Already-translated headline. */
	title: ReactNode;
	/** Already-translated supporting copy. Omit for a one-line banner. */
	description?: ReactNode;
	/**
	 * Element the description renders as. Default `"p"`. Pass `"div"` when the copy is itself
	 * block-level markup (a `<ul>` of per-item detail) — a list inside a `<p>` is invalid nesting
	 * and browsers silently close the paragraph around it, which breaks the banner's layout.
	 */
	descriptionComponent?: ElementType;
	/** Leading icon; auto-tinted to the tone's ink. */
	icon?: ReactNode;
	/** Semantic tone — drives fill / border / ink from theme tokens. Default `info`. */
	tone?: SystemBannerTone;
	/** Trailing slot for an action (e.g. a `SystemButton`) or a dismiss control. */
	action?: ReactNode;
	// DEFAULT: `stacked`, what every existing banner renders.
	layout?: SystemBannerLayout;
	// DEFAULT: `solid`, what every existing banner renders.
	emphasis?: SystemBannerEmphasis;
	/** Announces changes to assistive technology (`role="status"` on the text, never the action), for a banner that appears in answer to an action. */
	// DEFAULT: false = a static note (`role="note"`).
	live?: boolean;
	/** Layout overrides — the caller owns placement/width, not the banner's ink. */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

// Mount fade: opacity + a small lift. Transform/opacity only; reduced motion drops it.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes systemBannerEnter": {
		from: { opacity: 0, transform: "translateY(-4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `systemBannerEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * A tinted inline callout that explains something about the surface it sits on — a caveat, a
 * degraded data source, a "these numbers include an estimate" note.
 *
 * Generic over any page: it owns only the tone-to-token mapping and the icon/title/description/
 * action layout. Reach for this instead of a hand-rolled tinted `<Box>` + `<Typography>` pair, and
 * instead of MUI `<Alert>` (which brings its own severity palette rather than ours).
 *
 * For a transient outcome message use the snackbar; for "nothing here yet" use `EmptyState`.
 */
export const SystemBanner: FC<Props> = ({
	title,
	description,
	descriptionComponent = "p",
	icon,
	tone = "info",
	action,
	layout = "stacked",
	emphasis = "solid",
	live = false,
	sx,
	dataTestId,
}) => {
	const textRole = live ? "status" : undefined;
	return (
		<Box
			data-testid={dataTestId}
			role={live ? undefined : "note"}
			sx={[
				ENTRANCE_SX,
				(theme) => ({
					display: "flex",
					alignItems: "flex-start",
					gap: 1.25,
					px: 1.75,
					py: 1.25,
					borderRadius: 2,
					// A pending banner has no fill, so its dashed edge is drawn stronger to still frame it.
					border:
						emphasis === "pending"
							? `1px dashed ${alpha(theme.palette[tone].main, ALPHA.STATUS_BORDER)}`
							: `1px solid ${alpha(theme.palette[tone].main, SYSTEM_BANNER.BORDER_ALPHA)}`,
					backgroundColor: emphasis === "pending" ? theme.palette.background.paper : alpha(theme.palette[tone].main, FILL_ALPHA[emphasis]),
					color: theme.palette[tone].dark,
					"& > svg": { fontSize: 18, flexShrink: 0, mt: 0.125 },
				}),
				...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{icon}
			{layout === "inline" ? (
				<Typography component="p" role={textRole} sx={{ flex: 1, minWidth: 0, alignSelf: "center", fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45 }}>
					<Box component="strong" sx={{ fontWeight: 600 }}>
						{title}
					</Box>
					{description && <> {description}</>}
				</Typography>
			) : (
				<Box role={textRole} sx={{ flex: 1, minWidth: 0 }}>
					<Typography component="p" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, lineHeight: 1.35 }}>
						{title}
					</Typography>
					{description && (
						<Typography
							component={descriptionComponent}
							sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: LINE_HEIGHT.DENSE_PROSE, mt: 0.25, color: "inherit", opacity: 0.85 }}
						>
							{description}
						</Typography>
					)}
				</Box>
			)}
			{action && <Box sx={{ flexShrink: 0, alignSelf: layout === "inline" ? "center" : undefined }}>{action}</Box>}
		</Box>
	);
};

export default SystemBanner;
